import { useState, type FormEvent } from 'react';
import Reveal from '@/components/base/Reveal';

const reasons = [
  'Partnership',
  'Restaurant Opportunity',
  'Employment',
  'Vendor / Supplier',
  'General Inquiry',
];

const FORM_URL = 'https://readdy.ai/api/form/daiif1joh653ivfvodv0';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get('website_alt') as string) || '').trim();
    if (honeypot) {
      setStatus('success');
      setErrorMsg('');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value as string));

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const code = (parsed?.code as string) || '';
      const meta = parsed?.meta as Record<string, unknown> | undefined;

      if (res.ok && code === 'OK') {
        setStatus('success');
        setErrorMsg('');
        form.reset();
      } else {
        const serverMsg =
          (meta?.message as string) || (parsed?.message as string) || (meta?.detail as string) || responseText;
        setStatus('error');
        setErrorMsg(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  }

  return (
    <section id="contact" className="py-24 md:py-32 px-6 md:px-10 bg-background-50">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.35em] text-primary-600">Get In Touch</p>
          <h2 className="font-heading text-3xl md:text-5xl mt-6 text-foreground-950 leading-[1.1]">
            Have an idea, opportunity, or question?
          </h2>
          <p className="mt-7 text-foreground-700 leading-relaxed">
            Whether you&apos;re interested in working with JEDL, joining our team, or learning more about our
            restaurant concepts, we&apos;d be happy to hear from you.
          </p>

          <div className="mt-10 space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-foreground-400">Contact</p>
              <p className="mt-2 font-heading text-lg text-foreground-950">Johan Engman</p>
              <p className="text-foreground-500 text-sm">Owner</p>
            </div>

            <div className="space-y-3">
              <a
                href="mailto:johan@jedlrestaurants.com"
                className="flex items-center gap-3 text-foreground-800 hover:text-primary-600 transition-colors"
              >
                <span className="w-6 h-6 flex items-center justify-center text-foreground-400">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                johan@jedlrestaurants.com
              </a>
              <a
                href="tel:+16192832663"
                className="flex items-center gap-3 text-foreground-800 hover:text-primary-600 transition-colors"
              >
                <span className="w-6 h-6 flex items-center justify-center text-foreground-400">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                (619) 283-2663
              </a>
              <div className="flex items-start gap-3 text-foreground-800">
                <span className="w-6 h-6 flex items-center justify-center text-foreground-400 shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <span className="leading-relaxed">
                  5119 Cass St
                  <br />
                  San Diego, CA 92109-1812
                  <br />
                  United States
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <form
            id="jedl-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-background-100 border border-background-200 p-7 md:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-xs uppercase tracking-[0.15em] text-foreground-500 mb-2">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="Your name"
                  className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs uppercase tracking-[0.15em] text-foreground-500 mb-2">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="company" className="block text-xs uppercase tracking-[0.15em] text-foreground-500 mb-2">
                Company <span className="text-foreground-400 normal-case">(optional)</span>
              </label>
              <input
                id="company"
                name="company"
                type="text"
                placeholder="Company name"
                className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />
            </div>

            <div className="mt-5">
              <label htmlFor="reason" className="block text-xs uppercase tracking-[0.15em] text-foreground-500 mb-2">
                Reason for contacting
              </label>
              <div className="relative">
                <select
                  id="reason"
                  name="reason"
                  required
                  defaultValue=""
                  className="w-full appearance-none px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                >
                  <option value="" disabled>
                    Select a reason
                  </option>
                  {reasons.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-base"></i>
                </span>
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="block text-xs uppercase tracking-[0.15em] text-foreground-500 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="Tell us a little about your inquiry..."
                className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
              />
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="field-website"
            />

            <button
              type="submit"
              className="mt-7 whitespace-nowrap w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              Send Inquiry
              <i className="ri-send-plane-line text-base"></i>
            </button>

            {status === 'success' && (
              <p className="mt-5 text-sm text-accent-700 bg-accent-100/60 border border-accent-200 px-4 py-3 rounded-md">
                Thank you for reaching out. Your inquiry has been received — we&apos;ll be in touch soon.
              </p>
            )}
            {status === 'error' && (
              <p className="mt-5 text-sm text-primary-700 bg-primary-100/60 border border-primary-200 px-4 py-3 rounded-md">
                {errorMsg || 'Something went wrong. Please try again.'}
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}